import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
    Plus, Eye, Search, Calendar, Trash2, AlertTriangle, Pencil, Printer, CheckSquare
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialForm = {
    fecha: new Date().toISOString().split('T')[0],
    lote_id: '',
    relacion_corte: '',
    marca: '',
    tipo_pantalon: '',
    modelo: '',
    color_tela: '',
    color_hilo: '',
    descripcion: '',
    notas: '',
    cantidad_total: 0,
    piezas_cortadas: 0,
    cortador_id: '',
    cortador_nombre: '',
    bordado: false,
    serigrafia: false,
    observaciones: ''
};

const TALLAS_ADULTO = ['16', '28', '30', '32', '34', '36', '38', '40', '42', '44'];
const TALLAS_NINO = ['2', '4', '6', '8', '10', '12', '14', '16', '28'];
const TALLAS = TALLAS_ADULTO; // legacy default

export const Cortes = () => {
    const [cortes, setCortes] = useState([]);
    const [lotes, setLotes] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedCorte, setSelectedCorte] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [tallas, setTallas] = useState({});
    const [segmentoTallas, setSegmentoTallas] = useState('adulto'); // 'adulto' | 'nino'
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [editCortadorModal, setEditCortadorModal] = useState(null);
    const [editCortadorId, setEditCortadorId] = useState('');
    const [editMode, setEditMode] = useState(null); // corte being edited
    const [selected, setSelected] = useState(new Set());
    const [rangeFrom, setRangeFrom] = useState('');
    const [rangeTo, setRangeTo] = useState('');
    const [showPrint, setShowPrint] = useState(false);
    const printRef = useRef(null);

    const getEmpleadoFoto = (id) => {
        const emp = empleados.find(e => e.id === id);
        return emp?.foto_url || '';
    };

    const EmpAvatar = ({ nombre, empleadoId }) => {
        const foto = getEmpleadoFoto(empleadoId);
        const initials = nombre ? nombre.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() : '?';
        if (foto) return <img src={`${API_URL}${foto}`} alt={nombre} className="w-7 h-7 rounded-full object-cover border border-zinc-700 shrink-0" />;
        return <div className="w-7 h-7 rounded-full bg-amber-400/15 flex items-center justify-center text-[10px] font-bold text-amber-400 shrink-0">{initials}</div>;
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [cortesRes, lotesRes, empRes] = await Promise.all([
                axios.get(`${API_URL}/api/cortes`),
                axios.get(`${API_URL}/api/lotes`),
                axios.get(`${API_URL}/api/empleados`)
            ]);
            setCortes(cortesRes.data);
            setLotes(lotesRes.data);
            setEmpleados(empRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const cortadores = empleados.filter(e => e.tipo_trabajador === 'cortador');

    const handleTallaChange = (talla, cantidad) => {
        const newTallas = { ...tallas, [talla]: parseInt(cantidad) || 0 };
        setTallas(newTallas);
        const total = Object.values(newTallas).reduce((sum, val) => sum + val, 0);
        setForm(prev => ({ ...prev, cantidad_total: total }));
    };

    const handleCortadorChange = (empId) => {
        if (empId === 'por_asignar') {
            setForm(prev => ({ ...prev, cortador_id: 'por_asignar', cortador_nombre: 'Por asignar' }));
        } else {
            const emp = empleados.find(e => e.id === empId);
            setForm(prev => ({ ...prev, cortador_id: empId, cortador_nombre: emp ? emp.nombre : '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.relacion_corte || !form.marca || !form.cortador_id || !form.lote_id || !form.color_tela) {
            toast.error('Por favor complete los campos requeridos');
            return;
        }

        const relacion_tela_tallas = Object.entries(tallas)
            .filter(([_, cantidad]) => cantidad > 0)
            .map(([talla, cantidad]) => ({ talla, cantidad }));

        const payload = {
            fecha: form.fecha,
            lote_id: form.lote_id,
            relacion_corte: form.relacion_corte,
            marca: form.marca,
            modelo: form.modelo || '',
            tipo_pantalon: form.tipo_pantalon || '',
            color_tela: form.color_tela,
            color_hilo: form.color_hilo || '',
            descripcion: form.descripcion || '',
            notas: form.notas || '',
            relacion_tela_tallas,
            cantidad_total: form.cantidad_total,
            piezas_cortadas: form.piezas_cortadas || 0,
            cortador_id: form.cortador_id,
            cortador_nombre: form.cortador_nombre,
            bordado: form.bordado,
            serigrafia: form.serigrafia,
            observaciones: form.observaciones || ''
        };

        try {
            await axios.post(`${API_URL}/api/cortes`, payload);
            toast.success('Corte registrado exitosamente');
            setModalOpen(false);
            setForm(initialForm);
            setTallas({});
            fetchData();
        } catch (error) {
            console.error('Error saving corte:', error);
            toast.error(error.response?.data?.detail || 'Error al guardar corte');
        }
    };

    const handleView = (corte) => {
        setSelectedCorte(corte);
        setViewModalOpen(true);
    };

    const handleDeleteCorte = async (corteId) => {
        try {
            await axios.delete(`${API_URL}/api/cortes/${corteId}`);
            toast.success('Corte eliminado');
            setDeleteConfirm(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al eliminar');
            setDeleteConfirm(null);
        }
    };

    const handleEditCortador = async () => {
        if (!editCortadorModal) return;
        const cortador = editCortadorId === 'por_asignar'
            ? { cortador_id: 'por_asignar', cortador_nombre: 'Por asignar' }
            : { cortador_id: editCortadorId, cortador_nombre: empleados.find(e => e.id === editCortadorId)?.nombre || '' };
        try {
            await axios.put(`${API_URL}/api/cortes/${editCortadorModal.id}`, cortador);
            toast.success('Cortador actualizado');
            setEditCortadorModal(null);
            fetchData();
        } catch (error) { toast.error('Error al actualizar cortador'); }
    };

    const openEditModal = (corte) => {
        setEditMode(corte);
        setForm({
            fecha: corte.fecha || '',
            lote_id: corte.lote_id || '',
            relacion_corte: corte.relacion_corte || '',
            marca: corte.marca || '',
            tipo_pantalon: corte.tipo_pantalon || '',
            modelo: corte.modelo || '',
            color_tela: corte.color_tela || '',
            color_hilo: corte.color_hilo || '',
            descripcion: corte.descripcion || '',
            notas: corte.notas || '',
            cantidad_total: corte.cantidad_total || 0,
            piezas_cortadas: corte.piezas_cortadas || 0,
            cortador_id: corte.cortador_id || '',
            cortador_nombre: corte.cortador_nombre || '',
            bordado: corte.bordado || false,
            serigrafia: corte.serigrafia || false,
            observaciones: corte.observaciones || ''
        });
        const t = {};
        (corte.relacion_tela_tallas || []).forEach(item => { t[item.talla] = item.cantidad; });
        setTallas(t);
        // Auto-detectar segmento: si hay tallas EXCLUSIVAS del rango nino, mostrar pestania nino.
        // Las tallas ambiguas (presentes en ambos) no cuentan para la deteccion.
        const tallasUsadas = Object.keys(t);
        const ninoSoloSet = TALLAS_NINO.filter(x => !TALLAS_ADULTO.includes(x));
        const adultoSoloSet = TALLAS_ADULTO.filter(x => !TALLAS_NINO.includes(x));
        const hayNinoExclusivo = tallasUsadas.some(tt => ninoSoloSet.includes(tt));
        const hayAdultoExclusivo = tallasUsadas.some(tt => adultoSoloSet.includes(tt));
        setSegmentoTallas(hayNinoExclusivo && !hayAdultoExclusivo ? 'nino' : 'adulto');
        setModalOpen(true);
    };

    const handleEditSubmit = async () => {
        if (!editMode) return;
        const relacion_tela_tallas = Object.entries(tallas)
            .filter(([_, cantidad]) => cantidad > 0)
            .map(([talla, cantidad]) => ({ talla, cantidad }));
        const payload = {
            fecha: form.fecha,
            lote_id: form.lote_id,
            relacion_corte: form.relacion_corte,
            marca: form.marca,
            modelo: form.modelo || '',
            tipo_pantalon: form.tipo_pantalon || '',
            color_tela: form.color_tela,
            color_hilo: form.color_hilo || '',
            descripcion: form.descripcion || '',
            notas: form.notas || '',
            relacion_tela_tallas,
            cantidad_total: form.cantidad_total,
            piezas_cortadas: form.piezas_cortadas || 0,
            cortador_id: form.cortador_id,
            cortador_nombre: form.cortador_nombre,
            bordado: form.bordado,
            serigrafia: form.serigrafia,
            observaciones: form.observaciones || ''
        };
        try {
            await axios.put(`${API_URL}/api/cortes/${editMode.id}`, payload);
            toast.success('Corte actualizado exitosamente');
            setModalOpen(false);
            setEditMode(null);
            setForm(initialForm);
            setTallas({});
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al actualizar');
        }
    };

    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selected.size === filteredCortes.length) setSelected(new Set());
        else setSelected(new Set(filteredCortes.map(c => c.id)));
    };

    const applyRange = () => {
        if (!rangeFrom && !rangeTo) return;
        const fromLower = rangeFrom.toLowerCase();
        const toLower = rangeTo.toLowerCase();
        let inRange = false;
        const newSel = new Set(selected);
        filteredCortes.forEach(c => {
            const rel = (c.relacion_corte || '').toLowerCase();
            if (rel === fromLower || (!rangeFrom && !inRange)) inRange = true;
            if (inRange) newSel.add(c.id);
            if (rel === toLower) inRange = false;
        });
        setSelected(newSel);
        setRangeFrom('');
        setRangeTo('');
        toast.success(`${newSel.size} cortes seleccionados`);
    };

    const handlePrint = () => {
        if (selected.size === 0) { toast.error('Seleccione al menos un corte'); return; }
        setShowPrint(true);
        setTimeout(() => {
            const content = printRef.current;
            if (!content) return;
            const win = window.open('', '_blank');
            win.document.write(`<!DOCTYPE html><html><head><title>Fichas de Corte - Karry Jeans</title><style>
                @page { margin: 10mm; }
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: Arial, sans-serif; background: #fff; color: #111; }
                .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; padding: 8px; }
                .card { border: 2px solid #222; border-radius: 8px; padding: 12px; break-inside: avoid; page-break-inside: avoid; }
                .card-header { font-size: 18px; font-weight: bold; border-bottom: 2px solid #222; padding-bottom: 6px; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center; }
                .brand { font-size: 12px; font-weight: normal; color: #555; }
                .row { display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 13px; }
                .label { color: #555; }
                .value { font-weight: bold; }
                .tallas { font-size: 12px; font-weight: bold; background: #f0f0f0; padding: 4px 8px; border-radius: 4px; margin-bottom: 6px; text-align: center; }
                .title { text-align: center; font-size: 16px; font-weight: bold; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 6px; }
                .date { text-align: center; font-size: 11px; color: #777; margin-bottom: 10px; }
            </style></head><body>`);
            win.document.write(`<div class="title">FICHAS DE CORTE - KARRY JEAN'S</div>`);
            win.document.write(`<div class="date">Generado: ${new Date().toLocaleDateString('es-MX')}</div>`);
            win.document.write(`<div class="grid">`);
            selectedCortes.forEach(c => {
                const tallasStr = (c.relacion_tela_tallas || [])
                    .filter(t => t.cantidad > 0)
                    .map(t => `T${t.talla}: ${t.cantidad} paq`)
                    .join(' | ');
                win.document.write(`<div class="card">
                    <div class="card-header">${c.relacion_corte} <span class="brand">${c.marca}</span></div>
                    <div class="row"><span class="label">Piezas cortadas:</span><span class="value">${c.piezas_cortadas || c.cantidad_total || 0}</span></div>
                    <div class="row"><span class="label">Paquetes:</span><span class="value">${c.cantidad_total || 0}</span></div>
                    <div class="tallas">${tallasStr || '-'}</div>
                    <div class="row"><span class="label">Tipo de tela:</span><span class="value">${c.color_tela || '-'}</span></div>
                    <div class="row"><span class="label">Color de hilo:</span><span class="value">${c.color_hilo || '-'}</span></div>
                    <div class="row"><span class="label">Cortador:</span><span class="value">${c.cortador_nombre || '-'}</span></div>
                </div>`);
            });
            win.document.write(`</div></body></html>`);
            win.document.close();
            win.focus();
            win.print();
            setShowPrint(false);
        }, 100);
    };

    const getLoteNumero = (loteId) => {
        const lote = lotes.find(l => l.id === loteId);
        return lote ? lote.numero_lote : '-';
    };

    const filteredCortes = cortes.filter(corte =>
        (corte.relacion_corte || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (corte.marca || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (corte.modelo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (corte.cortador_nombre || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedCortes = filteredCortes.filter(c => selected.has(c.id));

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="cortes-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100" >
                        Formulario de Cortes
                    </h1>
                    <p className="text-zinc-400 mt-1">Registro y control de cortes de tela</p>
                </div>
                <Button
                    onClick={() => {
                        setForm(initialForm);
                        setTallas({});
                        setSegmentoTallas('adulto');
                        setEditMode(null);
                        setModalOpen(true);
                    }}
                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900"
                    data-testid="add-corte-btn"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Nuevo Corte
                </Button>
            </div>

            <Card className="card">
                <CardContent className="p-4 space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                        <Input
                            placeholder="Buscar por relación, marca, modelo o cortador..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                            data-testid="search-cortes"
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Input
                                placeholder="Desde (ej: C-005)"
                                value={rangeFrom}
                                onChange={(e) => setRangeFrom(e.target.value)}
                                className="h-9 w-32 text-xs"
                                data-testid="range-from"
                            />
                            <span className="text-zinc-500 text-xs">a</span>
                            <Input
                                placeholder="Hasta (ej: C-012)"
                                value={rangeTo}
                                onChange={(e) => setRangeTo(e.target.value)}
                                className="h-9 w-32 text-xs"
                                data-testid="range-to"
                            />
                            <Button size="sm" variant="outline" onClick={applyRange} className="h-9 text-xs" data-testid="apply-range-btn">
                                <CheckSquare className="w-3 h-3 mr-1" /> Rango
                            </Button>
                        </div>
                        {selected.size > 0 && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-amber-400 font-semibold">{selected.size} seleccionado(s)</span>
                                <Button size="sm" onClick={handlePrint} className="h-9 bg-zinc-700 hover:bg-zinc-600 text-xs" data-testid="print-selected-btn">
                                    <Printer className="w-3 h-3 mr-1" /> Imprimir fichas
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="h-9 text-xs text-zinc-400">
                                    Limpiar
                                </Button>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            <Card className="card">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800">
                                    <TableHead className="table-header w-10">
                                        <Checkbox checked={selected.size > 0 && selected.size === filteredCortes.length}
                                            onCheckedChange={toggleSelectAll} data-testid="select-all-cortes" />
                                    </TableHead>
                                    <TableHead className="table-header">Fecha</TableHead>
                                    <TableHead className="table-header">Lote</TableHead>
                                    <TableHead className="table-header">Relacion</TableHead>
                                    <TableHead className="table-header">Marca</TableHead>
                                    <TableHead className="table-header">Modelo</TableHead>
                                    <TableHead className="table-header">Cantidad</TableHead>
                                    <TableHead className="table-header">Cortador</TableHead>
                                    <TableHead className="table-header">Color Tela</TableHead>
                                    <TableHead className="table-header text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredCortes.length > 0 ? (
                                    filteredCortes.map((corte) => (
                                        <TableRow key={corte.id} className={`table-row ${selected.has(corte.id) ? 'bg-amber-400/5' : ''}`} data-testid={`corte-row-${corte.id}`}>
                                            <TableCell className="table-cell">
                                                <Checkbox checked={selected.has(corte.id)} onCheckedChange={() => toggleSelect(corte.id)}
                                                    data-testid={`select-corte-${corte.id}`} />
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="w-4 h-4 text-zinc-400" />
                                                    {corte.fecha}
                                                </div>
                                            </TableCell>
                                            <TableCell className="table-cell">{getLoteNumero(corte.lote_id)}</TableCell>
                                            <TableCell className="table-cell font-medium">{corte.relacion_corte}</TableCell>
                                            <TableCell className="table-cell">{corte.marca}</TableCell>
                                            <TableCell className="table-cell">{corte.modelo}</TableCell>
                                            <TableCell className="table-cell">
                                                <span className="badge-info">{corte.cantidad_total} paq</span>
                                                {corte.piezas_cortadas > 0 && (
                                                    <span className="text-xs text-zinc-500 ml-1">({corte.piezas_cortadas} pzas)</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                <div className="flex items-center gap-2">
                                                    <EmpAvatar nombre={corte.cortador_nombre} empleadoId={corte.cortador_id} />
                                                    <span className={corte.cortador_id === 'por_asignar' ? 'text-yellow-400 italic' : ''}>{corte.cortador_nombre}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                <span className="text-zinc-200">{corte.color_tela || '-'}</span>
                                            </TableCell>
                                            <TableCell className="table-cell text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => handleView(corte)} data-testid={`view-corte-${corte.id}`}>
                                                        <Eye className="w-4 h-4 text-zinc-400" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => openEditModal(corte)} title="Editar corte" data-testid={`edit-corte-${corte.id}`}>
                                                        <Pencil className="w-4 h-4 text-amber-400" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(corte)} data-testid={`delete-corte-${corte.id}`}>
                                                        <Trash2 className="w-4 h-4 text-red-400" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={10} className="text-center py-8 text-zinc-400">
                                            No se encontraron cortes
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Create/Edit Modal */}
            <Dialog open={modalOpen} onOpenChange={(open) => { setModalOpen(open); if (!open) setEditMode(null); }}>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="corte-modal">
                    <DialogHeader>
                        <DialogTitle>{editMode ? 'Editar Corte' : 'Nuevo Corte'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={editMode ? (e) => { e.preventDefault(); handleEditSubmit(); } : handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Fecha *</Label>
                                <Input
                                    type="date"
                                    value={form.fecha}
                                    onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                                    data-testid="input-fecha"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Lote *</Label>
                                <Select value={form.lote_id} onValueChange={(v) => setForm({ ...form, lote_id: v })}>
                                    <SelectTrigger data-testid="input-lote">
                                        <SelectValue placeholder="Seleccionar lote" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {lotes.map(l => (
                                            <SelectItem key={l.id} value={l.id}>{l.numero_lote} - {l.marca}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Relación de Corte *</Label>
                                <Input
                                    value={form.relacion_corte}
                                    onChange={(e) => setForm({ ...form, relacion_corte: e.target.value })}
                                    placeholder="Ej: C-001"
                                    data-testid="input-relacion"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Marca *</Label>
                                <Input
                                    value={form.marca}
                                    onChange={(e) => setForm({ ...form, marca: e.target.value })}
                                    placeholder="Marca"
                                    data-testid="input-marca"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Modelo</Label>
                                <Input
                                    value={form.modelo}
                                    onChange={(e) => setForm({ ...form, modelo: e.target.value })}
                                    placeholder="Modelo"
                                    data-testid="input-modelo"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Tipo de Pantalón</Label>
                                <Input
                                    value={form.tipo_pantalon}
                                    onChange={(e) => setForm({ ...form, tipo_pantalon: e.target.value })}
                                    placeholder="Tipo"
                                    data-testid="input-tipo"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Color de Tela *</Label>
                                <Input
                                    value={form.color_tela}
                                    onChange={(e) => setForm({ ...form, color_tela: e.target.value })}
                                    placeholder="Ej: Azul"
                                    data-testid="input-color-tela"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                <Label className="form-label !mb-0">Relacion de Tela y Tallas (Paquetes)</Label>
                                <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-1">
                                    <button
                                        type="button"
                                        onClick={() => setSegmentoTallas('adulto')}
                                        className={`px-3 py-1 rounded-md text-xs font-medium transition ${segmentoTallas === 'adulto' ? 'bg-amber-400 text-zinc-900' : 'text-zinc-400 hover:text-zinc-200'}`}
                                        data-testid="btn-talla-adulto"
                                    >Adulto (28-44)</button>
                                    <button
                                        type="button"
                                        onClick={() => setSegmentoTallas('nino')}
                                        className={`px-3 py-1 rounded-md text-xs font-medium transition ${segmentoTallas === 'nino' ? 'bg-amber-400 text-zinc-900' : 'text-zinc-400 hover:text-zinc-200'}`}
                                        data-testid="btn-talla-nino"
                                    >Niño (2-16)</button>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 gap-3 p-4 bg-zinc-800 rounded-lg">
                                {(segmentoTallas === 'nino' ? TALLAS_NINO : TALLAS_ADULTO).map((talla) => (
                                    <div key={talla} className="flex items-center gap-2">
                                        <Label className="text-sm text-zinc-400 w-8">T{talla}</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={tallas[talla] || ''}
                                            onChange={(e) => handleTallaChange(talla, e.target.value)}
                                            className="h-9"
                                            placeholder="paq"
                                            data-testid={`input-talla-${talla}`}
                                        />
                                    </div>
                                ))}
                            </div>
                            {/* Mostrar tallas seleccionadas del otro segmento como chips informativos */}
                            {(() => {
                                const otherSet = segmentoTallas === 'nino' ? TALLAS_ADULTO : TALLAS_NINO;
                                const filledOther = Object.entries(tallas).filter(([t, v]) => otherSet.includes(t) && v > 0);
                                if (filledOther.length === 0) return null;
                                return (
                                    <div className="mt-2 p-2 rounded bg-zinc-800/50 flex items-center gap-2 flex-wrap">
                                        <span className="text-[10px] text-zinc-500">{segmentoTallas === 'nino' ? 'Adulto' : 'Niño'}:</span>
                                        {filledOther.map(([t, v]) => (
                                            <span key={t} className="badge-info text-[10px]">T{t}: {v}</span>
                                        ))}
                                    </div>
                                );
                            })()}
                            <p className="text-sm text-zinc-400 mt-2">
                                Total: <span className="font-semibold text-amber-400">{form.cantidad_total} paquetes</span>
                            </p>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Piezas cortadas por el cortador *</Label>
                            <Input
                                type="number"
                                min="0"
                                value={form.piezas_cortadas || ''}
                                onChange={(e) => setForm({ ...form, piezas_cortadas: parseInt(e.target.value) || 0 })}
                                placeholder="Total de piezas que corto el cortador"
                                data-testid="input-piezas-cortadas"
                            />
                            {form.cantidad_total > 0 && form.piezas_cortadas > 0 && (
                                <div className="mt-3 p-3 bg-zinc-800 rounded-lg space-y-2">
                                    <p className="text-sm text-zinc-300">
                                        Piezas por paquete: <span className="font-bold text-amber-400">{(form.piezas_cortadas / form.cantidad_total).toFixed(2)}</span>
                                    </p>
                                    <p className="text-xs text-zinc-500 mb-2">Cierres por talla (auto-calculado):</p>
                                    <div className="flex flex-wrap gap-2">
                                        {Object.entries(tallas).filter(([_, v]) => v > 0).map(([talla, paq]) => {
                                            const ppp = form.piezas_cortadas / form.cantidad_total;
                                            const cierres = Math.round(ppp * paq);
                                            return (
                                                <span key={talla} className="badge-info text-xs">
                                                    T{talla}: {paq} paq = {cierres} cierres
                                                </span>
                                            );
                                        })}
                                    </div>
                                    <p className="text-sm text-zinc-300 mt-1">
                                        Total cierres: <span className="font-bold text-emerald-400">
                                            {Object.entries(tallas).filter(([_, v]) => v > 0).reduce((sum, [_, paq]) => sum + Math.round((form.piezas_cortadas / form.cantidad_total) * paq), 0)}
                                        </span>
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Cortador *</Label>
                                <Select value={form.cortador_id} onValueChange={handleCortadorChange}>
                                    <SelectTrigger data-testid="input-cortador">
                                        <SelectValue placeholder="Seleccionar cortador" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="por_asignar" className="text-yellow-400 italic">Por asignar</SelectItem>
                                        {cortadores.map(e => (
                                            <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Color de Hilo</Label>
                                <Input
                                    value={form.color_hilo}
                                    onChange={(e) => setForm({ ...form, color_hilo: e.target.value })}
                                    placeholder="Color"
                                    data-testid="input-hilo"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Detalles del Producto</Label>
                            <div className="flex gap-6 p-4 bg-zinc-800 rounded-lg">
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="bordado"
                                        checked={form.bordado}
                                        onCheckedChange={(checked) => setForm({ ...form, bordado: checked })}
                                        data-testid="checkbox-bordado"
                                    />
                                    <Label htmlFor="bordado" className="cursor-pointer">Bordado</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="serigrafia"
                                        checked={form.serigrafia}
                                        onCheckedChange={(checked) => setForm({ ...form, serigrafia: checked })}
                                        data-testid="checkbox-serigrafia"
                                    />
                                    <Label htmlFor="serigrafia" className="cursor-pointer">Serigrafía</Label>
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Observaciones</Label>
                            <Textarea
                                value={form.observaciones}
                                onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                                placeholder="Notas adicionales"
                                rows={2}
                                data-testid="input-observaciones"
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => { setModalOpen(false); setEditMode(null); }}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="save-corte-btn">
                                {editMode ? 'Actualizar Corte' : 'Guardar Corte'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* View Modal */}
            <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
                <DialogContent className="sm:max-w-[600px]" data-testid="view-corte-modal">
                    <DialogHeader>
                        <DialogTitle >Detalle del Corte</DialogTitle>
                    </DialogHeader>
                    {selectedCorte && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-zinc-400">Fecha</p>
                                    <p className="font-medium">{selectedCorte.fecha}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-400">Lote</p>
                                    <p className="font-medium">{getLoteNumero(selectedCorte.lote_id)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-400">Relación</p>
                                    <p className="font-medium">{selectedCorte.relacion_corte}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-400">Marca</p>
                                    <p className="font-medium">{selectedCorte.marca}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-400">Modelo</p>
                                    <p className="font-medium">{selectedCorte.modelo || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-400">Color Tela</p>
                                    <p className="font-medium">{selectedCorte.color_tela || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-400">Color Hilo</p>
                                    <p className="font-medium">{selectedCorte.color_hilo || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-400">Total Paquetes</p>
                                    <p className="font-medium text-amber-400">{selectedCorte.cantidad_total} paquetes</p>
                                </div>
                                {selectedCorte.piezas_cortadas > 0 && (
                                    <div>
                                        <p className="text-sm text-zinc-400">Piezas Cortadas</p>
                                        <p className="font-medium text-emerald-400">{selectedCorte.piezas_cortadas} piezas</p>
                                    </div>
                                )}
                                {selectedCorte.piezas_por_paquete > 0 && (
                                    <div>
                                        <p className="text-sm text-zinc-400">Piezas por Paquete</p>
                                        <p className="font-medium">{selectedCorte.piezas_por_paquete}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-sm text-zinc-400">Cortador</p>
                                    <p className="font-medium">{selectedCorte.cortador_nombre}</p>
                                </div>
                            </div>
                            
                            {selectedCorte.relacion_tela_tallas?.length > 0 && (
                                <div>
                                    <p className="text-sm text-zinc-400 mb-2">Desglose por Tallas (Paquetes)</p>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedCorte.relacion_tela_tallas.map((t, i) => (
                                            <span key={i} className="badge-info">
                                                T{t.talla}: {t.cantidad} paq
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedCorte.cierres_por_talla?.length > 0 && (
                                <div>
                                    <p className="text-sm text-zinc-400 mb-2">Cierres por Talla (calculado)</p>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedCorte.cierres_por_talla.map((t, i) => (
                                            <span key={i} className="px-2 py-1 rounded-md bg-emerald-400/10 text-emerald-400 text-xs font-medium">
                                                T{t.talla}: {t.cierres} cierres
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-2">
                                {selectedCorte.bordado && <span className="badge-success">Con Bordado</span>}
                                {selectedCorte.serigrafia && <span className="badge-warning">Con Serigrafía</span>}
                            </div>

                            {selectedCorte.observaciones && (
                                <div>
                                    <p className="text-sm text-zinc-400">Observaciones</p>
                                    <p className="font-medium">{selectedCorte.observaciones}</p>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                <DialogContent className="sm:max-w-[400px]" data-testid="delete-corte-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-400">
                            <AlertTriangle className="w-5 h-5" /> Eliminar Corte
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-zinc-300">Eliminar corte <strong>{deleteConfirm?.relacion_corte}</strong>?</p>
                    <p className="text-xs text-zinc-500">No se puede eliminar si tiene movimientos o maquilas asociadas.</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                        <Button className="bg-red-500 hover:bg-red-600" onClick={() => handleDeleteCorte(deleteConfirm.id)} data-testid="confirm-delete-corte">Eliminar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Hidden Print ref */}
            <div ref={printRef} className="hidden" />
        </div>
    );
};
